import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { api, type BackupRow } from '../lib/api'
import { formatBytes, formatRelative } from '../lib/format'
import { Spinner, Tag, EmptyState } from '../components/ui/Primitives'

export default function Backups() {
  const { data, isLoading } = useQuery({
    queryKey: ['backups-all'],
    queryFn: () => api.get<BackupRow[]>('/backups'),
  })

  if (isLoading) return <Spinner className="h-6 w-6" />

  const verificados = (data ?? []).filter((b) => b.status === 'VERIFIED').length
  const fallados = (data ?? []).filter((b) => b.status === 'FAILED').length

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Backups</h1>
        <p className="hint mt-0.5">
          {data?.length ?? 0} en total · {verificados} verificado(s)
          {fallados > 0 && ` · ${fallados} fallado(s)`}
        </p>
      </header>

      {(data ?? []).length === 0 ? (
        <EmptyState
          title="Todavía no hay backups"
          hint="Se crean desde la pestaña Backups de cada instancia."
        />
      ) : (
        <div className="card divide-y divide-slate-800/60 p-0">
          {(data ?? []).map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <Link to={`/instancias/${b.deploymentId}`} className="text-sm hover:text-blue-400">
                  {b.deployment?.app.slug} / {b.deployment?.name}
                </Link>
                <p className="hint mt-0.5 truncate font-mono text-xs">{b.location}</p>
              </div>
              <Tag>{b.kind.toLowerCase()}</Tag>
              {b.deployment?.protected && <Tag tone="amber">protegida</Tag>}
              <span className={clsx(
                'rounded px-1.5 py-0.5 text-[11px] font-medium',
                b.status === 'VERIFIED' ? 'bg-green-500/10 text-green-400'
                  : b.status === 'SUCCESS' ? 'bg-blue-500/10 text-blue-400'
                  : b.status === 'FAILED' ? 'bg-red-500/10 text-red-400'
                  : 'bg-slate-500/10 text-slate-400',
              )}>
                {b.status === 'VERIFIED' ? 'verificado' : b.status.toLowerCase()}
              </span>
              <span className="hint w-20 text-right">{formatBytes(b.sizeBytes)}</span>
              <span className="hint w-28 text-right">{formatRelative(b.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
